import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Text,
  HStack,
  IconButton,
  Select,
  Radio,
  RadioGroup,
  Stack,
  Divider,
  Button,
} from '@chakra-ui/react';
import { MdAdd, MdDelete } from 'react-icons/md';
import type { MasterDataFieldDefinition } from '@/lib/sdk/master-data-field-definitions/types';
import type { WritebackMode } from '@/lib/sdk/chatbot/types';
import { ValueRefPicker } from './ValueRefPicker';
import type { ValueRefTask } from './valueRefTasks';

/** One independent writeback policy's draft state — a task can configure several,
 * each patching its own dataset/row (e.g. one stamping route_process.completed_at,
 * another advancing route_card.current_stage). */
export interface WritebackPolicyDraft {
  dataset: string;
  /** Undefined means "key", the default. */
  matchField?: string;
  /** Undefined means "update", the default. */
  mode?: WritebackMode;
  keyRef: string;
  /** Value references for an added row's label and Belongs To. */
  labelRef: string;
  parentKeyRef: string;
  fieldEntries: { field: string; value: string }[];
}

export interface WritebackPolicyErrors {
  dataset?: string;
  keyRef?: string;
  labelRef?: string;
  fields?: string;
}

interface WritebackConfigSectionProps {
  policies: WritebackPolicyDraft[];
  onAddPolicy: () => void;
  onRemovePolicy: (index: number) => void;
  onPolicyChange: (index: number, patch: Partial<WritebackPolicyDraft>) => void;
  /** Tasks the value pickers list. */
  valueRefTasks: ValueRefTask[];
  /** Dataset names from ListMasterDataDatasets: categories plus datasets that have rows. */
  masterDataDatasets: string[];
  /** Field definitions keyed by dataset name, covering every policy's currently selected dataset — each policy can target a different one. */
  fieldDefsByDataset: Record<string, MasterDataFieldDefinition[]>;
  errors?: WritebackPolicyErrors[];
  /** Shown when WRITEBACK is enabled but no policy has been added yet. */
  noPoliciesError?: string;
}

export function WritebackConfigSection({
  policies,
  onAddPolicy,
  onRemovePolicy,
  onPolicyChange,
  valueRefTasks,
  masterDataDatasets,
  fieldDefsByDataset,
  errors,
  noPoliciesError,
}: WritebackConfigSectionProps) {
  return (
    <Box>
      <VStack align="stretch" spacing={4}>
        <FormLabel color="text-primary" fontSize="md" fontWeight="medium" mb={0}>
          Master Data Writeback Configuration
        </FormLabel>
        <Text fontSize="sm" color="text-secondary">
          On completion, patch fields onto a master_data row — e.g. advancing a route card
          to the next process — or add a new row. Only the listed fields are touched;
          everything else on the row is left alone. Add more than one policy to write
          several rows (even in different datasets) from the same completion — each runs
          independently, so one failing doesn't stop the others.
        </Text>

        {policies.map((policy, index) => (
          <Box key={index}>
            {index > 0 && <Divider mb={4} borderColor="border-default" />}
            <WritebackPolicyFields
              index={index}
              policy={policy}
              onChange={(patch) => onPolicyChange(index, patch)}
              onRemove={policies.length > 1 ? () => onRemovePolicy(index) : undefined}
              valueRefTasks={valueRefTasks}
              masterDataDatasets={masterDataDatasets}
              availableFields={fieldDefsByDataset[policy.dataset] ?? []}
              errors={errors?.[index]}
            />
          </Box>
        ))}

        {noPoliciesError && policies.length === 0 && (
          <Text fontSize="sm" color="red.500">{noPoliciesError}</Text>
        )}

        <Button
          leftIcon={<MdAdd />}
          size="sm"
          variant="outline"
          colorScheme="brand"
          alignSelf="flex-start"
          onClick={onAddPolicy}
          data-testid="wfv4-config-writeback-add-policy-btn"
        >
          {policies.length === 0 ? 'Add a writeback policy' : 'Add another writeback policy'}
        </Button>
      </VStack>
    </Box>
  );
}

interface WritebackPolicyFieldsProps {
  index: number;
  policy: WritebackPolicyDraft;
  onChange: (patch: Partial<WritebackPolicyDraft>) => void;
  /** Omitted when this is the only policy — at least one must always remain. */
  onRemove?: () => void;
  valueRefTasks: ValueRefTask[];
  masterDataDatasets: string[];
  availableFields: MasterDataFieldDefinition[];
  errors?: WritebackPolicyErrors;
}

function WritebackPolicyFields({
  index,
  policy,
  onChange,
  onRemove,
  valueRefTasks,
  masterDataDatasets,
  availableFields,
  errors,
}: WritebackPolicyFieldsProps) {
  const { dataset, keyRef, fieldEntries } = policy;
  const mode = policy.mode ?? 'update';
  const addsRows = mode !== 'update';
  const effectiveMatchField = policy.matchField || 'key';
  const labelFromMatch = mode === 'upsert' && effectiveMatchField === 'label';
  const matchFieldInList = effectiveMatchField === 'key'
    || effectiveMatchField === 'label'
    || availableFields.some((f) => f.field_name === effectiveMatchField);

  const setFieldEntries = (entries: { field: string; value: string }[]) => onChange({ fieldEntries: entries });

  return (
    <Box bg="bg-surface" p={4} rounded="md" borderWidth="1px" borderColor="border-default">
      {onRemove && (
        <HStack justify="space-between" mb={2}>
          <Text fontSize="xs" color="text-muted" fontWeight="medium">Policy {index + 1}</Text>
          <IconButton
            aria-label="Remove this writeback policy"
            icon={<MdDelete />}
            size="xs"
            variant="ghost"
            colorScheme="red"
            onClick={onRemove}
            data-testid={`wfv4-config-writeback-remove-policy-${index}`}
          />
        </HStack>
      )}

      <VStack align="stretch" spacing={3}>
        <FormControl isInvalid={!!errors?.dataset}>
          <FormLabel fontSize="sm" color="text-primary" mb={1}>
            Target Dataset
          </FormLabel>
          <Select
            placeholder="Select a dataset"
            size="sm"
            value={dataset}
            onChange={(e) => onChange({ dataset: e.target.value })}
            bg="bg-surface"
            borderColor="border-default"
            data-testid={`wfv4-config-writeback-dataset-${index}`}
          >
            {dataset && !masterDataDatasets.includes(dataset) && (
              <option value={dataset}>{dataset} (not a defined dataset)</option>
            )}
            {masterDataDatasets.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </Select>
          <Text fontSize="xs" color="text-muted" mt={1} data-testid={`wfv4-config-writeback-dataset-hint-${index}`}>
            Lists datasets and master data categories, including ones with no rows yet. Unless
            rows are added below, the dataset needs its rows before going live.
          </Text>
          <FormErrorMessage>{errors?.dataset}</FormErrorMessage>
        </FormControl>

        {dataset && (
          <FormControl>
            <FormLabel fontSize="sm" color="text-primary" mb={1}>
              When No Row Matches
            </FormLabel>
            <RadioGroup
              value={mode}
              onChange={(val) => onChange({ mode: val === 'update' ? undefined : (val as WritebackMode) })}
            >
              <Stack spacing={1}>
                <Radio value="update" size="sm" data-testid={`wfv4-config-writeback-mode-update-${index}`}>
                  Skip — only update rows that exist
                </Radio>
                <Radio value="upsert" size="sm" data-testid={`wfv4-config-writeback-mode-upsert-${index}`}>
                  Add a new row
                </Radio>
                <Radio value="create" size="sm" data-testid={`wfv4-config-writeback-mode-create-${index}`}>
                  Always add a new row, without looking for a match
                </Radio>
              </Stack>
            </RadioGroup>
            <Text fontSize="xs" color="text-muted" mt={1}>
              A new row must pass the dataset&apos;s field rules (required fields, select
              options, number and date formats), and is never added over a deactivated row.
              Values are written as resolved: {'{now}'} is a full timestamp, so stamp it into a
              text field, not a date field.
            </Text>
          </FormControl>
        )}

        {dataset && mode !== 'create' && (
          <FormControl>
            <FormLabel fontSize="sm" color="text-primary" mb={1}>
              Match On
            </FormLabel>
            <RadioGroup
              value={effectiveMatchField}
              onChange={(val) => onChange({ matchField: val === 'key' ? undefined : val })}
            >
              <Stack spacing={1}>
                {!matchFieldInList && (
                  <Radio value={effectiveMatchField} size="sm">
                    {effectiveMatchField} (not a defined field)
                  </Radio>
                )}
                <Radio value="key" size="sm" data-testid={`wfv4-config-writeback-matchfield-key-${index}`}>
                  Key
                </Radio>
                <Radio value="label" size="sm" data-testid={`wfv4-config-writeback-matchfield-label-${index}`}>
                  Label
                </Radio>
                {availableFields.map((f) => (
                  <Radio
                    key={f.field_name}
                    value={f.field_name}
                    size="sm"
                    data-testid={`wfv4-config-writeback-matchfield-${f.field_name}-${index}`}
                  >
                    {f.label} ({f.field_name})
                  </Radio>
                ))}
              </Stack>
            </RadioGroup>
            <Text fontSize="xs" color="text-muted" mt={1}>
              Which field the value below is matched against to find the row to patch.
            </Text>
          </FormControl>
        )}

        <FormControl isInvalid={!!errors?.keyRef}>
          <FormLabel fontSize="sm" color="text-primary" mb={1}>
            {mode === 'create' ? 'New Row Key' : 'Match Value Reference'}
          </FormLabel>
          <ValueRefPicker
            value={keyRef}
            onChange={(value) => onChange({ keyRef: value })}
            tasks={valueRefTasks}
            placeholder={mode === 'create' ? 'Leave empty to generate a key' : 'Choose a value'}
            isInvalid={!!errors?.keyRef}
            testId={`wfv4-config-writeback-keyref-${index}`}
          />
          <Text fontSize="xs" color="text-muted" mt={1}>
            {mode === 'create'
              ? 'The new row\'s key, or empty to generate one. A key already in the dataset is not added again.'
              : `The value matched against the field selected above. Choose Source key when the
            target row's key is the physical item that was scanned (e.g. the route card's own
            source key).${mode === 'upsert' ? ' A new row stores this value as its key, label or field.' : ''}`}
          </Text>
          <FormErrorMessage>{errors?.keyRef}</FormErrorMessage>
        </FormControl>

        {addsRows && !labelFromMatch && (
          <FormControl isInvalid={!!errors?.labelRef}>
            <FormLabel fontSize="sm" color="text-primary" mb={1}>
              New Row Label
            </FormLabel>
            <ValueRefPicker
              value={policy.labelRef}
              onChange={(value) => onChange({ labelRef: value })}
              tasks={valueRefTasks}
              isInvalid={!!errors?.labelRef}
              testId={`wfv4-config-writeback-labelref-${index}`}
            />
            <Text fontSize="xs" color="text-muted" mt={1}>
              The label of a row this policy adds. An existing row&apos;s
              label is never changed.
            </Text>
            <FormErrorMessage>{errors?.labelRef}</FormErrorMessage>
          </FormControl>
        )}

        {addsRows && (
          <FormControl>
            <FormLabel fontSize="sm" color="text-primary" mb={1}>
              New Row Belongs To (optional)
            </FormLabel>
            <ValueRefPicker
              value={policy.parentKeyRef}
              onChange={(value) => onChange({ parentKeyRef: value })}
              tasks={valueRefTasks}
              placeholder="Optional"
              testId={`wfv4-config-writeback-parentkeyref-${index}`}
            />
            <Text fontSize="xs" color="text-muted" mt={1}>
              The parent key of a row this policy adds.
            </Text>
          </FormControl>
        )}
      </VStack>

      <FormControl mt={4} isInvalid={!!errors?.fields}>
        <HStack justify="space-between" mb={2}>
          <FormLabel fontSize="sm" color="text-primary" mb={0}>
            Fields to Patch
          </FormLabel>
          <IconButton
            aria-label="Add writeback field"
            icon={<MdAdd />}
            size="xs"
            colorScheme="brand"
            isDisabled={!dataset}
            onClick={() => setFieldEntries([...fieldEntries, { field: '', value: '' }])}
            data-testid={`wfv4-config-writeback-add-btn-${index}`}
          />
        </HStack>
        <Text fontSize="xs" color="text-muted" mb={2}>
          Besides task answers, a field can be stamped with the Current time at completion
          (e.g. when this stage finished) or the Source key that triggered it (e.g. which
          route card).
        </Text>

        {fieldEntries.length > 0 ? (
          <VStack spacing={2} align="stretch">
            {fieldEntries.map((entry, fieldIndex) => (
              <HStack key={fieldIndex} spacing={2} align="flex-start">
                <Select
                  placeholder="Select field"
                  size="sm"
                  value={entry.field}
                  onChange={(e) => {
                    const next = [...fieldEntries];
                    next[fieldIndex] = { ...next[fieldIndex], field: e.target.value };
                    setFieldEntries(next);
                  }}
                  bg="bg-surface"
                  borderColor="border-default"
                  flex={1}
                  data-testid={`wfv4-config-writeback-field-${index}-${fieldIndex}`}
                >
                  {entry.field && !availableFields.some((f) => f.field_name === entry.field) && (
                    <option value={entry.field}>{entry.field} (not a defined field)</option>
                  )}
                  {availableFields.map((f) => (
                    <option key={f.field_name} value={f.field_name}>{f.label} ({f.field_name})</option>
                  ))}
                </Select>
                <Box flex={2} minW={0}>
                  <ValueRefPicker
                    value={entry.value}
                    onChange={(value) => {
                      const next = [...fieldEntries];
                      next[fieldIndex] = { ...next[fieldIndex], value };
                      setFieldEntries(next);
                    }}
                    tasks={valueRefTasks}
                    testId={`wfv4-config-writeback-value-${index}-${fieldIndex}`}
                  />
                </Box>
                <IconButton
                  aria-label="Remove writeback field"
                  icon={<MdDelete />}
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => setFieldEntries(fieldEntries.filter((_, i) => i !== fieldIndex))}
                  data-testid={`wfv4-config-writeback-remove-${index}-${fieldIndex}`}
                />
              </HStack>
            ))}
          </VStack>
        ) : (
          <Text fontSize="xs" color="text-muted" fontStyle="italic">
            {dataset
              ? 'No fields configured. Click + to add one.'
              : 'Select a target dataset first.'}
          </Text>
        )}
        {dataset && availableFields.length === 0 && (
          <Text fontSize="xs" color="text-muted" mt={2}>
            No field definitions on this dataset yet — add one under Master Data field
            definitions, then it will appear here.
          </Text>
        )}
        <FormErrorMessage>{errors?.fields}</FormErrorMessage>
      </FormControl>
    </Box>
  );
}
